import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatCitation, ChatHistoryMessage, streamChat, ChatRequestBody } from '../services/chat/chat-sse';
import {
  loadChatTurns,
  saveChatTurns,
  clearChatTurns,
  DEFAULT_MAX_BYTES,
} from '../services/chat/chat-storage';

export interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
  citations?: ChatCitation[];
  interrupted?: boolean;
}

export interface UseChatStreamOptions {
  /** When set, the conversation is cached in sessionStorage under this key so it
   *  survives in-session navigation and reloads. Omit for in-memory-only. */
  storageKey?: string;
  /** Max serialized size before oldest turns are evicted. */
  maxBytes?: number;
}

export interface UseChatStream {
  turns: ChatTurn[];
  streaming: boolean;
  error: string | null;
  send: (question: string, build: (history: ChatHistoryMessage[]) => ChatRequestBody) => Promise<void>;
  reset: () => void;
}

/**
 * Ephemeral chat state. History lives in React state and is sent in full on each
 * turn (no server-side persistence). When `storageKey` is provided the running
 * turns are mirrored into sessionStorage so the conversation is restored after
 * in-session navigation (e.g. opening a citation) or a page reload. `build` maps
 * the running history into the API request body (org/team/scope come from the caller).
 */
export function useChatStream(options: UseChatStreamOptions = {}): UseChatStream {
  const { storageKey, maxBytes = DEFAULT_MAX_BYTES } = options;
  const [turns, setTurns] = useState<ChatTurn[]>(() =>
    storageKey ? loadChatTurns(storageKey) : [],
  );
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const loadedKeyRef = useRef<string | undefined>(storageKey);

  // When the conversation identity changes (e.g. viewId resolves after mount, or
  // a different chat block), load that key's persisted turns.
  useEffect(() => {
    if (storageKey === loadedKeyRef.current) return;
    loadedKeyRef.current = storageKey;
    setTurns(storageKey ? loadChatTurns(storageKey) : []);
  }, [storageKey]);

  // Persist completed turns. Skip mid-stream to avoid a write per token; the
  // finished exchange is saved when `streaming` flips back to false.
  useEffect(() => {
    if (!storageKey || streaming) return;
    saveChatTurns(storageKey, turns, maxBytes);
  }, [turns, streaming, storageKey, maxBytes]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setTurns([]);
    setError(null);
    setStreaming(false);
    if (storageKey) clearChatTurns(storageKey);
  }, [storageKey]);

  const send = useCallback<UseChatStream['send']>(async (question, build) => {
    if (streaming || !question.trim()) return;
    setError(null);

    const userTurn: ChatTurn = { role: 'user', text: question };
    const historyForApi: ChatHistoryMessage[] = [...turns, userTurn].map((t) => ({ role: t.role, text: t.text }));
    setTurns((prev) => [...prev, userTurn, { role: 'assistant', text: '' }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      let assistant = '';
      let citations: ChatCitation[] = [];
      for await (const frame of streamChat(build(historyForApi), controller.signal)) {
        if (frame.error) {
          setError(frame.error);
          setTurns((prev) => markLastAssistant(prev, assistant, citations, true));
          break;
        }
        if (frame.delta) {
          assistant += frame.delta;
          setTurns((prev) => markLastAssistant(prev, assistant, citations, false));
        }
        if (frame.done) {
          citations = frame.citations ?? [];
          setTurns((prev) => markLastAssistant(prev, assistant, citations, false));
          break;
        }
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setError((e as Error).message);
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [streaming, turns]);

  return { turns, streaming, error, send, reset };
}

function markLastAssistant(
  prev: ChatTurn[],
  text: string,
  citations: ChatCitation[],
  interrupted: boolean,
): ChatTurn[] {
  const next = [...prev];
  for (let i = next.length - 1; i >= 0; i--) {
    if (next[i].role === 'assistant') {
      next[i] = { ...next[i], text, citations, interrupted };
      break;
    }
  }
  return next;
}
